// ============================================================================
// MÓDULO: BATCH IMPORT PANEL — PLANILHA DE LANÇAMENTO RÁPIDO (V3.5 — 15/06/2026)
// Larguras calculadas por conteúdo típico máximo (fonte 9px, ~5.5px/caractere + padding + borda)
// ============================================================================
import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  TEMPLATE_MAP,
  ALL_TEMPLATE_IDS,
  ACCOUNT_OPTIONS,
  PETS,
} from "../domain/templates.js";
import { DEFAULT_CATEGORIES } from "../domain/categories.js";
import { normalizeTaxonomyValue } from "./TaxonomyCombobox.jsx";

const todayISO = () => new Date().toISOString().slice(0, 10);
const currMonth = () => new Date().toISOString().slice(0, 7);
const formatCompDate = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return `${m}/${y}`;
};
const fromBr = (v) => String(v ?? "").replace(",", ".");
const toBr = (v) => String(v ?? "").replace(".", ",");

const STATUS_CONFIG = {
  pending: { label: "", cls: "" },
  done: {
    label: "✅ Lançado",
    cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  error: { label: "❌ Erro", cls: "bg-red-50 text-red-700 border-red-200" },
};

const EMPTY_ROW = () => ({
  id: `batch-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  docId: null,
  group: "ALIMENTACAO",
  subCategory: "",
  templateId: "",
  natureza: "PF",
  sudNivel1: "",
  sudNivel2: "",
  sudNivel3: "",
  sudVars: "",
  descIn: "",
  descOut: "",
  account: "C6 PJ (Pix)",
  valorPrevisto: "",
  valorReal: "",
  date: todayISO(),
  juros: "0,00",
  desconto: "0,00",
  acrescimo: "0,00",
  tarifa: "0,00",
  modoLancamento: "UNICO",
  parcelaNumero: "",
  parcelaTotal: "",
  recorrencia: "Mensal",
  repeticoes: "12",
  numeroDocumento: "",
  dataEmissao: "",
  competenceDate: currMonth(),
  settlementDate: "",
  statusType: "PAID",
  needsSettlement: false,
  status: "pending",
  errorMsg: "",
  vinculo: "",
});

// SSOT no Firestore (prop `categories`) — DEFAULT_CATEGORIES fica só de
// fallback pra quando ainda não carregou (mesmo padrão do PostingModal).
const buildGroupOptions = (categories) =>
  Object.entries(categories || DEFAULT_CATEGORIES).map(([k, v]) => ({
    value: k,
    label: v.label || k,
    nature: v.nature || "EXPENSE",
  }));
const getSubsForGroup = (categories, g) =>
  (categories || DEFAULT_CATEGORIES)[g]?.subs || [];

const buildTemplateOptions = () => {
  if (!TEMPLATE_MAP) return [];
  return ALL_TEMPLATE_IDS.filter(
    (id) => TEMPLATE_MAP[id]?.active !== false,
  ).map((id) => {
    const t = TEMPLATE_MAP[id];
    const s = t?.suds || {};
    return {
      value: id,
      label: id,
      group: t.group || "",
      sub: t?.sub || "",
      descIn: t?.descIn || "",
      descOut: t?.descOut || "",
      account: t?.account || "C6 PJ (Pix)",
      amount: t?.amount || t?.valorMin || 0,
      parcelado: t?.parcelado || false,
      recorrente: t?.recorrente || false,
      parcelaTotal: t?.parcelaTotal || null,
      suds: s,
    };
  });
};

/** Exporta as linhas atuais como CSV */
const exportBatchCSV = (rows, filename = "importacao_lote.csv") => {
  if (!rows || !rows.length) return;
  const headers = [
    "Data",
    "Grupo",
    "Subcat.",
    "Template",
    "Fornecedor",
    "Serviço",
    "Beneficiário",
    "Conta",
    "Previsto",
    "Real",
    "Juros",
    "Desconto",
    "Acréscimo",
    "Tarifa",
    "Modo",
    "Parcela",
    "Situação",
    "Competência",
    "Nº Doc.",
    "Emissão",
    "Pagamento",
    "Vínculo",
  ];
  const data = rows.map((r) => [
    r.date,
    r.group,
    r.subCategory,
    r.templateId,
    r.sudNivel1,
    r.sudNivel2,
    r.sudNivel3,
    r.account,
    r.valorPrevisto,
    r.valorReal,
    r.juros,
    r.desconto,
    r.acrescimo,
    r.tarifa,
    r.modoLancamento,
    r.modoLancamento === "PARCELADO"
      ? `${r.parcelaNumero}/${r.parcelaTotal}`
      : "",
    r.statusType,
    r.competenceDate,
    r.numeroDocumento,
    r.dataEmissao,
    r.settlementDate,
    r.vinculo,
  ]);
  const csv = [headers.join(","), ...data.map((e) => e.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const buildSudOptions = () => {
  const n1 = new Set(),
    n2 = new Set(),
    n3 = new Set();
  Object.values(TEMPLATE_MAP).forEach((t) => {
    if (t.suds?.nivel1) n1.add(t.suds.nivel1);
    if (t.suds?.nivel2) n2.add(t.suds.nivel2);
    if (t.suds?.nivel3) n3.add(t.suds.nivel3);
  });
  return {
    nivel1: [...n1].sort(),
    nivel2: [...n2].sort(),
    nivel3: [...n3].sort(),
  };
};

/** Parseia CSV e retorna array de rows para loadRows */
const importCSV = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) return resolve([]);
        const rawHeaders = lines[0].split(/[;,]/).map((h) =>
          h
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""),
        );
        const headerMap = {
          data: "date",
          venc: "date",
          grupo: "group",
          subcat: "subCategory",
          subcategoria: "subCategory",
          template: "templateId",
          id: "templateId",
          fornecedor: "sudNivel1",
          i: "sudNivel1",
          servico: "sudNivel2",
          ii: "sudNivel2",
          beneficiario: "sudNivel3",
          iii: "sudNivel3",
          benef: "sudNivel3",
          conta: "account",
          prev: "valorPrevisto",
          previsto: "valorPrevisto",
          real: "valorReal",
          juros: "juros",
          desc: "desconto",
          desconto: "desconto",
          acr: "acrescimo",
          acrescimo: "acrescimo",
          tar: "tarifa",
          tarifa: "tarifa",
          modo: "modoLancamento",
          sit: "statusType",
          situacao: "statusType",
          compet: "competenceDate",
          competencia: "competenceDate",
          ndoc: "numeroDocumento",
          "n doc": "numeroDocumento",
          emissao: "dataEmissao",
          pagamento: "settlementDate",
          pgto: "settlementDate",
          vinculo: "vinculo",
          natureza: "natureza",
          parcela: "parcelaNumero",
          parctotal: "parcelaTotal",
          recorrencia: "recorrencia",
          repeticoes: "repeticoes",
          descricao: "descOut",
          descout: "descOut",
          descin: "descIn",
        };
        const headers = rawHeaders.map((h) => headerMap[h] || null);
        const parsed = [];
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i]
            .split(/[;,]/)
            .map((c) => c.trim().replace(/^"(.*)"$/, "$1"));
          const row = {};
          headers.forEach((field, idx) => {
            if (field && cols[idx] !== undefined) row[field] = cols[idx];
          });
          if (row.date || row.group || row.valorReal) parsed.push(row);
        }
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Falha ao ler arquivo"));
    reader.readAsText(file);
  });
};

export const BatchImportPanel = ({
  externalRows,
  onSaveBatch,
  onSaveTemplateLabel,
  categories,
  sud,
  onAddSudValue,
}) => {
  const [rows, setRows] = useState([]);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const fileInputRef = React.useRef(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [promptText, setPromptText] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [templateLabels, setTemplateLabels] = useState({});
  const [confirmedTemplates, setConfirmedTemplates] = useState({});
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [customSud, setCustomSud] = useState({});

  const templateOptions = useMemo(buildTemplateOptions, []);
  const staticSudFallback = useMemo(buildSudOptions, []);
  const sudOptions = {
    nivel1: sud?.nivel1?.length ? sud.nivel1 : staticSudFallback.nivel1,
    nivel2: sud?.nivel2?.length ? sud.nivel2 : staticSudFallback.nivel2,
    nivel3: sud?.nivel3?.length ? sud.nivel3 : staticSudFallback.nivel3,
  };
  const groupOptions = useMemo(() => buildGroupOptions(categories), [categories]);

  // Ao confirmar um valor de SUD digitado à mão (botão ✓ "Voltar pro
  // dropdown"), persiste no Firestore se for realmente novo (comparação
  // normalizada — evita duplicar "Netflix" x "netflix" x "Netflix "), pra
  // aparecer como opção pronta na próxima vez, no lote e no lançamento
  // individual (mesma fonte única de verdade).
  const persistCustomSud = (nivel, value) => {
    const trimmed = (value || "").trim();
    if (!trimmed || typeof onAddSudValue !== "function") return;
    const list = sudOptions[`nivel${nivel}`] || [];
    const alreadyExists = list.some(
      (v) => normalizeTaxonomyValue(v) === normalizeTaxonomyValue(trimmed),
    );
    if (!alreadyExists) onAddSudValue(nivel, trimmed);
  };

  // Carrega linhas externas (ex: do OCR) quando recebe atualizacao
  useEffect(() => {
    if (externalRows && externalRows.length > 0) {
      loadRows(externalRows);
    }
  }, [externalRows]);

  const addRow = useCallback(() => setRows((p) => [...p, EMPTY_ROW()]), []);

  const updateRow = useCallback(
    (id, field, value) => {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== id) return r;
          if (field === "templateId") {
            const tpl = templateOptions.find((t) => t.value === value);
            if (tpl) {
              const s = tpl.suds || {};
              const isR = tpl.group === "RECEITAS";
              return {
                ...r,
                templateId: value,
                group: tpl.group || r.group,
                subCategory: tpl.sub || "",
                sudNivel1: s.nivel1 || "",
                sudNivel2: s.nivel2 || "",
                sudNivel3: s.nivel3 || "",
                sudVars: s.vars || "",
                descIn: tpl.descIn,
                descOut: tpl.descOut,
                account: tpl.account || r.account,
                valorPrevisto: toBr(String(tpl.amount || "")),
                natureza: isR ? "PJ" : r.natureza,
                modoLancamento: tpl.parcelado
                  ? "PARCELADO"
                  : tpl.recorrente
                    ? "RECORRENTE"
                    : "UNICO",
                parcelaTotal: tpl.parcelaTotal ? String(tpl.parcelaTotal) : "",
              };
            }
          }
          if (field.startsWith("sud")) {
            const u = { ...r, [field]: value };
            if (field === "sudNivel1") {
              u.sudNivel2 = "";
              u.sudNivel3 = "";
            }
            if (field === "sudNivel2") {
              u.sudNivel3 = "";
            }
            const n1 = u.sudNivel1 || "",
              n2 = u.sudNivel2 || "",
              n3 = u.sudNivel3 || "";
            const vars = u.sudVars ? ` (${u.sudVars})` : "";
            // Mesmo padrão do modal individual (18/09/2026): descrição é o
            // reflexo direto do SUD, "Nível 1 — Nível 2 — Nível 3".
            if (n1) {
              const partes = [n1, n2, n3].filter(Boolean).join(" — ");
              u.descIn = partes;
              u.descOut = `${partes}${vars}`;
            }
            return u;
          }
          if (field === "group") return { ...r, group: value, subCategory: "" };
          return { ...r, [field]: value };
        }),
      );
    },
    [templateOptions],
  );

  const loadRows = useCallback(
    (newRows) => {
      setRows(
        newRows.map((r, i) => {
          const tpl = r.templateId
            ? templateOptions.find((t) => t.value === r.templateId)
            : null;
          const s = tpl?.suds || {};
          return {
            ...EMPTY_ROW(),
            id: r.id || `batch-${Date.now()}-${i}`,
            docId: r.docId || r.id || null,
            group: r.group || tpl?.group || "ALIMENTACAO",
            subCategory: r.subCategory || r.sub || tpl?.sub || "",
            templateId: r.templateId || r.template || "",
            vinculo: r.vinculo || "",
            natureza: r.natureza || "PF",
            sudNivel1: r.sudNivel1 || s.nivel1 || "",
            sudNivel2: r.sudNivel2 || s.nivel2 || "",
            sudNivel3: r.sudNivel3 || s.nivel3 || "",
            sudVars: r.sudVars || s.vars || "",
            descIn: r.descIn || tpl?.descIn || "",
            descOut: r.descOut || r.descricao || tpl?.descOut || "",
            account: r.account || tpl?.account || "C6 PJ (Pix)",
            valorPrevisto:
              r.valorPrevisto != null
                ? r.valorPrevisto
                : tpl?.amount
                  ? toBr(String(tpl.amount))
                  : "",
            valorReal: r.valorReal ?? r.value ?? r.valor ?? "",
            date: r.date || todayISO(),
            juros: r.juros ?? "0,00",
            desconto: r.desconto ?? "0,00",
            acrescimo: r.acrescimo ?? "0,00",
            tarifa: r.tarifa ?? "0,00",
            modoLancamento: r.modoLancamento || "UNICO",
            parcelaNumero: r.parcelaNumero || "",
            parcelaTotal: r.parcelaTotal || "",
            recorrencia: r.recorrencia || "Mensal",
            repeticoes: r.repeticoes || "12",
            numeroDocumento: r.numeroDocumento || "",
            dataEmissao: r.dataEmissao || "",
            competenceDate: (r.competenceDate || "").slice(0, 7) || currMonth(),
            settlementDate: r.settlementDate || "",
            statusType: r.statusType || "PAID",
            needsSettlement: r.needsSettlement || false,
            status: "pending",
          };
        }),
      );
    },
    [templateOptions],
  );

  const handleParsePrompt = useCallback(() => {
    if (!promptText.trim()) return;
    try {
      const d = JSON.parse(promptText);
      if (Array.isArray(d)) {
        loadRows(d);
        setShowPromptModal(false);
        setPromptText("");
        return;
      }
      if (d?.rows) {
        loadRows(d.rows);
        setShowPromptModal(false);
        setPromptText("");
        return;
      }
    } catch (_) {
      /* not JSON, try parse as table */
    }
    const parsed = [];
    for (const line of promptText
      .trim()
      .split("\n")
      .filter((l) => l.trim())) {
      if (
        line.match(/^\|?[\s\-:]+\|/) ||
        line.match(/^\|?\s*(#|Data|Item)\s*\|/i)
      )
        continue;
      let cols;
      if (line.includes("|"))
        cols = line
          .split("|")
          .map((c) => c.trim())
          .filter((c) => c);
      else if (line.includes("\t"))
        cols = line.split("\t").map((c) => c.trim());
      else if (line.includes(";")) cols = line.split(";").map((c) => c.trim());
      else cols = line.split(",").map((c) => c.trim());
      if (cols.length < 2) continue;
      const v = parseFloat(cols[2]?.replace(/[R$\s.*]/g, "").replace(",", "."));
      if (isNaN(v)) continue;
      parsed.push({
        date: cols[1]?.replace(/\*/g, "").trim() || todayISO(),
        value: v,
        beneficiary: cols[3]?.replace(/\*/g, "").trim() || "",
        natureza: (cols[4] || "PF").replace(/\*/g, "").trim().toUpperCase(),
        templateId: cols[5]?.replace(/\*/g, "").trim() || "",
      });
    }
    if (parsed.length) {
      loadRows(parsed);
      setShowPromptModal(false);
      setPromptText("");
    }
  }, [promptText, loadRows]);

  const handleConfirmAll = useCallback(async () => {
    const pending = rows.filter((r) => r.status !== "done");
    if (!pending.length) return;
    setSaving(true);
    setSaveResult(null);
    let ok = 0,
      fail = 0;
    const updated = [...rows];
    for (const row of pending) {
      const idx = updated.findIndex((r) => r.id === row.id);
      try {
        const amount =
          Math.round(Number(fromBr(row.valorReal || "0")) * 100) / 100;
        const isPF = row.natureza !== "PJ";
        await onSaveBatch({
          id: row.docId || null,
          forceSingle: true,
          templateId: row.templateId || null,
          type: row.group,
          group: row.group,
          subCategory: row.subCategory,
          repeatMode:
            row.modoLancamento === "PARCELADO"
              ? "INSTALLMENT"
              : row.modoLancamento === "RECORRENTE"
                ? "RECURRING"
                : "SINGLE",
          repeatCount:
            row.modoLancamento === "PARCELADO"
              ? parseInt(row.parcelaTotal) || 0
              : parseInt(row.repeticoes) || 0,
          description:
            row.descIn ||
            [row.sudNivel1, row.sudNivel2, row.sudNivel3]
              .filter(Boolean)
              .join(" — "),
          descOut: row.descOut,
          account: row.account,
          amount,
          valorPrevisto: parseFloat(fromBr(row.valorPrevisto)) || 0,
          date: row.date,
          competenceDate: row.competenceDate || row.date?.slice(0, 7),
          juros: parseFloat(fromBr(row.juros)) || 0,
          desconto: parseFloat(fromBr(row.desconto)) || 0,
          acrescimo: parseFloat(fromBr(row.acrescimo)) || 0,
          tarifa: parseFloat(fromBr(row.tarifa)) || 0,
          modoLancamento: row.modoLancamento,
          parcelaNumero: row.parcelaNumero ? parseInt(row.parcelaNumero) : null,
          parcelaTotal: row.parcelaTotal ? parseInt(row.parcelaTotal) : null,
          recorrencia:
            row.modoLancamento === "RECORRENTE" ? row.recorrencia : "Única",
          repetirMeses:
            row.modoLancamento === "RECORRENTE" ? parseInt(row.repeticoes) : 0,
          isPaid: row.statusType === "PAID",
          statusType: row.statusType,
          needsSettlement: row.needsSettlement,
          settlementDate: row.settlementDate || null,
          numeroDocumento: row.numeroDocumento || null,
          dataEmissao: row.dataEmissao || null,
          sudNivel1: row.sudNivel1 || null,
          sudNivel2: row.sudNivel2 || null,
          sudNivel3: row.sudNivel3 || null,
          sudVars: row.sudVars || null,
          vinculo: row.vinculo || null,
        });
        if (isPF) {
          const b = row.sudNivel3 || row.sudNivel1 || "Despesa PF";
          await onSaveBatch({
            type: "RECEITAS",
            group: "RECEITAS",
            subCategory: "Retirada PJ Recta Scripta",
            description: `Retirada PJ Recta Scripta → ${b}`,
            descOut: `${row.sudNivel1 || "Fornecedor"} — Retirada PJ (compensa ${b})`,
            account: row.account,
            amount,
            valorPrevisto: amount,
            date: row.date,
            competenceDate: row.competenceDate || row.date?.slice(0, 7),
            juros: 0,
            desconto: 0,
            acrescimo: 0,
            tarifa: 0,
            modoLancamento: "UNICO",
            recorrencia: "Única",
            repetirMeses: 0,
            isPaid: row.statusType === "PAID",
            statusType: row.statusType,
            needsSettlement: false,
            settlementDate: row.settlementDate || null,
            sudNivel1: "Recta Scripta Editora",
            sudNivel2: "Retirada PJ Recta Scripta",
            sudNivel3: b,
            sudVars: `Compensa ${row.group || ""} — ${row.subCategory || ""}`,
            vinculo: row.vinculo || null,
          });
        }
        updated[idx] = { ...updated[idx], status: "done" };
        ok++;
      } catch (e) {
        updated[idx] = {
          ...updated[idx],
          status: "error",
          errorMsg: e.message || "Erro",
        };
        fail++;
      }
    }
    setRows(updated);
    setSaveResult({ ok, fail, total: pending.length });
    setSaving(false);
  }, [rows, onSaveBatch]);

  const pendingCount = rows.filter((r) => r.status === "pending").length;
  const doneCount = rows.filter((r) => r.status === "done").length;
  const errorCount = rows.filter((r) => r.status === "error").length;

  const Th = ({ children, className = "" }) => (
    <th
      className={`px-0.5 py-1.5 text-[8px] font-bold text-[#6b7280] uppercase tracking-tight ${className}`}
    >
      {children}
    </th>
  );
  const In = ({
    type = "text",
    value,
    onChange,
    disabled,
    placeholder = "",
    className = "",
    maxWidth,
    ...rest
  }) => {
    const input = (
      <input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        {...rest}
        className={`w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151] ${className}`}
      />
    );
    return (
      <td className="px-0.5 py-0.5">
        {maxWidth ? (
          <div
            className="flex items-center gap-0.5 overflow-hidden"
            style={{ maxWidth }}
          >
            {input}
          </div>
        ) : (
          input
        )}
      </td>
    );
  };

  const TD = "px-0.5 py-0.5";
  const SEL =
    "w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151] disabled:appearance-none truncate";

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-[13px] font-bold text-[#20243B]">
            <i className="fas fa-table text-[11px] mr-1" />
            Planilha de Lançamento Rápido
          </h3>
          <span className="text-[10px] text-[#6b7280]">
            {rows.length} linha{rows.length !== 1 ? "s" : ""}
            {pendingCount > 0 && (
              <>
                {" "}
                · {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
              </>
            )}
            {doneCount > 0 && (
              <>
                {" "}
                · {doneCount} concluído{doneCount !== 1 ? "s" : ""}
              </>
            )}
            {errorCount > 0 && (
              <>
                {" "}
                · {errorCount} erro{errorCount !== 1 ? "s" : ""}
              </>
            )}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setShowPromptModal(true)}
            className="px-2 py-1 text-[10px] font-semibold text-white bg-[#20243B] hover:bg-[#1a1e30] rounded-md flex items-center gap-1 transition-colors"
          >
            <i className="fas fa-robot text-[9px]" />
            Preencher via IA
          </button>
          <button
            onClick={() => exportBatchCSV(rows)}
            disabled={rows.length === 0}
            className="px-2 py-1 text-[10px] font-semibold text-[#059669] hover:bg-[#ecfdf5] border border-[#a7f3d0] rounded-md flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="fas fa-file-excel text-[9px]" />
            Exportar CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              setCsvImporting(true);
              try {
                const parsed = await importCSV(file);
                if (parsed.length > 0) loadRows(parsed);
                else alert("Nenhuma linha válida encontrada no CSV.");
              } catch (err) {
                alert("Erro ao importar CSV: " + err.message);
              } finally {
                setCsvImporting(false);
                e.target.value = "";
              }
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={csvImporting}
            className="px-2 py-1 text-[10px] font-semibold text-[#20243B] hover:bg-[#f3f4f6] border border-[#d1d5db] rounded-md flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {csvImporting ? (
              <i className="fas fa-spinner fa-spin text-[9px]" />
            ) : (
              <i className="fas fa-file-import text-[9px]" />
            )}
            Importar CSV
          </button>
        </div>
      </div>
      {saveResult && (
        <div
          className={`p-2 rounded-md text-[11px] font-medium ${saveResult.fail === 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}
        >
          {saveResult.ok}/{saveResult.total} lançados com sucesso
          {saveResult.fail > 0 && <> · {saveResult.fail} falhas</>}
          <button
            onClick={() => setSaveResult(null)}
            className="ml-2 text-[10px] underline hover:no-underline"
          >
            Fechar
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-[#e5e7eb] rounded-lg">
        <table
          className="w-full text-left border-separate border-spacing-0 table-fixed"
          style={{ fontSize: "9px" }}
        >
          <thead className="bg-[#f3f4f6] sticky top-0 z-50">
            <tr>
              <Th className="w-[28px] text-center">#</Th>
              <Th className="w-[79px]">Venc.</Th>
              <Th className="w-[160px]">Grupo</Th>
              <Th className="w-[140px]">Subcat.</Th>
              <Th className="w-[92px]">T</Th>
              <Th className="w-[126px]">I — Fornecedor</Th>
              <Th className="w-[130px]">II — Serviço</Th>
              <Th className="w-[120px]">III — Benef.</Th>
              <Th className="w-[82px]">Conta</Th>
              <Th className="w-[54px]">Prev.</Th>
              <Th className="w-[54px]">Real</Th>
              <Th className="w-[54px]">Juros</Th>
              <Th className="w-[54px]">Desc.</Th>
              <Th className="w-[54px]">Acr.</Th>
              <Th className="w-[54px]">Tar.</Th>
              <Th className="w-[60px]">Modo</Th>
              <Th className="w-[128px]">Parc.</Th>
              <Th className="w-[60px]">Sit.</Th>
              <Th className="w-[52px]">Compet.</Th>
              <Th className="w-[48px]">Nº Doc.</Th>
              <Th className="w-[79px]">Emissão</Th>
              <Th className="w-[79px]">Pgto. Efet.</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan="22"
                  className="p-6 text-center text-[#9ca3af] text-[11px]"
                >
                  <i className="fas fa-info-circle mr-1" />
                  Nenhum lançamento. Clique em{" "}
                  <strong>"Preencher via IA"</strong> ou <strong>"+"</strong>.
                </td>
              </tr>
            )}
            {rows.map((row, idx) => {
              const subs = getSubsForGroup(categories, row.group);
              const bg =
                row.status === "done"
                  ? "bg-[#f0fdf4]"
                  : row.status === "error"
                    ? "bg-[#fef2f2]"
                    : "bg-white";
              const filteredN2 = row.sudNivel1
                ? [
                    ...new Set(
                      Object.values(TEMPLATE_MAP)
                        .filter(
                          (t) =>
                            t.suds?.nivel1 === row.sudNivel1 && t.suds?.nivel2,
                        )
                        .map((t) => t.suds.nivel2),
                    ),
                  ].sort()
                : sudOptions.nivel2;
              const filteredN3 =
                row.sudNivel1 || row.sudNivel2
                  ? [
                      ...new Set(
                        Object.values(TEMPLATE_MAP)
                          .filter(
                            (t) =>
                              (!row.sudNivel1 ||
                                t.suds?.nivel1 === row.sudNivel1) &&
                              (!row.sudNivel2 ||
                                t.suds?.nivel2 === row.sudNivel2) &&
                              t.suds?.nivel3,
                          )
                          .map((t) => t.suds.nivel3),
                      ),
                    ].sort()
                  : sudOptions.nivel3;

              return (
                <tr
                  key={row.id}
                  className={`${bg} hover:bg-gray-50/50 transition-colors group`}
                >
                  <td
                    className={`${TD} text-center text-[#9ca3af] text-[8px] font-mono`}
                  >
                    {idx + 1}
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "79px" }}
                    >
                      <input
                        type="date"
                        value={row.date}
                        onChange={(e) =>
                          updateRow(row.id, "date", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className="w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                      />
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "160px" }}
                    >
                      <select
                        value={row.group}
                        onChange={(e) =>
                          updateRow(row.id, "group", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className={`${SEL} truncate`}
                        style={{ maxWidth: "158px" }}
                      >
                        {groupOptions.map((g) => (
                          <option
                            key={g.value}
                            value={g.value}
                          >{`${g.nature === "INCOME" ? "🟢" : "🔴"} ${g.label}`}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "128px" }}
                    >
                      <select
                        value={row.subCategory}
                        onChange={(e) =>
                          updateRow(row.id, "subCategory", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className={`${SEL} truncate`}
                        style={{ maxWidth: "138px" }}
                      >
                        <option value="">—</option>
                        {subs.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "92px" }}
                    >
                      <select
                        value={row.templateId}
                        onChange={(e) =>
                          updateRow(row.id, "templateId", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className="flex-1 pl-1 pr-4 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151] disabled:appearance-none truncate"
                        style={{ maxWidth: "78px" }}
                      >
                        <option value="">—</option>
                        {templateOptions.map((t) => (
                          <option key={t.value} value={t.value}>
                            {templateLabels[row.id]?.[t.value] ?? t.label}
                          </option>
                        ))}
                      </select>
                      {confirmedTemplates[row.id]?.[row.templateId] ? (
                        <span
                          className="text-[12px] flex-shrink-0"
                          title="Template atualizado"
                        >
                          ✅
                        </span>
                      ) : (
                        <button
                          onClick={() => {
                            if (!row.templateId) return;
                            const n1 = row.sudNivel1 || "Fornecedor";
                            const n2 = row.sudNivel2 || "Serviço";
                            const n3 = row.sudNivel3 || "Beneficiário";
                            setConfirmTarget({
                              rowId: row.id,
                              templateId: row.templateId,
                              newLabel: `${row.templateId} — ${n1} — ${n2} — ${n3}`,
                              n1,
                              n2,
                              n3,
                            });
                          }}
                          disabled={row.status === "done" || !row.templateId}
                          title="Atualizar nome do template com SUD atual"
                          className="px-1 py-1 text-[10px] text-[#20243B] hover:bg-[#C5A059]/20 rounded transition disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          ↻
                        </button>
                      )}
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "126px" }}
                    >
                      {customSud[row.id]?.n1 ? (
                        <>
                          <input
                            value={row.sudNivel1}
                            onChange={(e) =>
                              updateRow(row.id, "sudNivel1", e.target.value)
                            }
                            disabled={row.status === "done"}
                            placeholder="Fornecedor"
                            className="flex-1 px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                          />
                          <button
                            onClick={() => {
                              persistCustomSud(1, row.sudNivel1);
                              setCustomSud((p) => ({
                                ...p,
                                [row.id]: { ...p[row.id], n1: false },
                              }));
                            }}
                            disabled={row.status === "done"}
                            className="px-1 py-1 text-[10px] text-green-600 hover:bg-green-50 rounded transition flex-shrink-0"
                            title="Voltar para dropdown"
                          >
                            ✓
                          </button>
                        </>
                      ) : (
                        <>
                          <select
                            value={row.sudNivel1}
                            onChange={(e) =>
                              updateRow(row.id, "sudNivel1", e.target.value)
                            }
                            disabled={row.status === "done"}
                            className={SEL}
                          >
                            <option value="">—</option>
                            {sudOptions.nivel1.map((v) => (
                              <option key={v} value={v}>
                                {v.length > 14 ? v.slice(0, 13) + "…" : v}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() =>
                              setCustomSud((p) => ({
                                ...p,
                                [row.id]: { ...p[row.id], n1: true },
                              }))
                            }
                            disabled={row.status === "done"}
                            className="px-1 py-1 text-[7px] text-[#6b7280] hover:bg-gray-100 rounded transition flex-shrink-0"
                            title="Digitar valor personalizado"
                          >
                            ✏️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "130px" }}
                    >
                      {customSud[row.id]?.n2 ? (
                        <>
                          <input
                            value={row.sudNivel2}
                            onChange={(e) =>
                              updateRow(row.id, "sudNivel2", e.target.value)
                            }
                            disabled={row.status === "done"}
                            placeholder="Serviço"
                            className="flex-1 px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                          />
                          <button
                            onClick={() => {
                              persistCustomSud(2, row.sudNivel2);
                              setCustomSud((p) => ({
                                ...p,
                                [row.id]: { ...p[row.id], n2: false },
                              }));
                            }}
                            disabled={row.status === "done"}
                            className="px-1 py-1 text-[7px] text-green-600 hover:bg-green-50 rounded transition flex-shrink-0"
                            title="Voltar para dropdown"
                          >
                            ✓
                          </button>
                        </>
                      ) : (
                        <>
                          <select
                            value={row.sudNivel2}
                            onChange={(e) =>
                              updateRow(row.id, "sudNivel2", e.target.value)
                            }
                            disabled={row.status === "done"}
                            className={SEL}
                          >
                            <option value="">—</option>
                            {filteredN2.map((v) => (
                              <option key={v} value={v}>
                                {v.length > 14 ? v.slice(0, 13) + "…" : v}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() =>
                              setCustomSud((p) => ({
                                ...p,
                                [row.id]: { ...p[row.id], n2: true },
                              }))
                            }
                            disabled={row.status === "done"}
                            className="px-1 py-1 text-[7px] text-[#6b7280] hover:bg-gray-100 rounded transition flex-shrink-0"
                            title="Digitar valor personalizado"
                          >
                            ✏️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "120px" }}
                    >
                      {customSud[row.id]?.n3 ? (
                        <>
                          <input
                            value={row.sudNivel3}
                            onChange={(e) =>
                              updateRow(row.id, "sudNivel3", e.target.value)
                            }
                            disabled={row.status === "done"}
                            placeholder="Beneficiário"
                            className="flex-1 px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                          />
                          <button
                            onClick={() => {
                              persistCustomSud(3, row.sudNivel3);
                              setCustomSud((p) => ({
                                ...p,
                                [row.id]: { ...p[row.id], n3: false },
                              }));
                            }}
                            disabled={row.status === "done"}
                            className="px-1 py-1 text-[7px] text-green-600 hover:bg-green-50 rounded transition flex-shrink-0"
                            title="Voltar para dropdown"
                          >
                            ✓
                          </button>
                        </>
                      ) : (
                        <>
                          <select
                            value={row.sudNivel3}
                            onChange={(e) =>
                              updateRow(row.id, "sudNivel3", e.target.value)
                            }
                            disabled={row.status === "done"}
                            className={SEL}
                          >
                            <option value="">—</option>
                            {filteredN3.map((v) => (
                              <option key={v} value={v}>
                                {v.length > 14 ? v.slice(0, 13) + "…" : v}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() =>
                              setCustomSud((p) => ({
                                ...p,
                                [row.id]: { ...p[row.id], n3: true },
                              }))
                            }
                            disabled={row.status === "done"}
                            className="px-1 py-1 text-[7px] text-[#6b7280] hover:bg-gray-100 rounded transition flex-shrink-0"
                            title="Digitar valor personalizado"
                          >
                            ✏️
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "82px" }}
                    >
                      <select
                        value={row.account}
                        onChange={(e) =>
                          updateRow(row.id, "account", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className={`${SEL} truncate`}
                        style={{ maxWidth: "80px" }}
                      >
                        <option value="">—</option>
                        {(ACCOUNT_OPTIONS || []).map((a) => (
                          <option key={a} value={a}>
                            {a.length > 16 ? a.slice(0, 15) + "…" : a}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "54px" }}
                    >
                      <input
                        type="text"
                        value={
                          row.valorPrevisto != null && row.valorPrevisto !== ""
                            ? Number(
                                String(row.valorPrevisto).replace(",", "."),
                              ).toLocaleString("pt-BR", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })
                            : ""
                        }
                        disabled
                        className="w-full px-1 py-1 text-[9px] text-right font-mono border border-gray-200 rounded bg-white text-gray-800 outline-none"
                      />
                    </div>
                  </td>
                  <In
                    type="text"
                    inputMode="decimal"
                    value={row.valorReal}
                    onChange={(e) =>
                      updateRow(row.id, "valorReal", e.target.value)
                    }
                    disabled={row.status === "done"}
                    placeholder="0,00"
                    className="text-right font-mono"
                    maxWidth="54px"
                  />
                  <In
                    value={row.juros}
                    onChange={(e) => updateRow(row.id, "juros", e.target.value)}
                    disabled={row.status === "done"}
                    placeholder="0,00"
                    className="text-right font-mono"
                    maxWidth="54px"
                  />
                  <In
                    value={row.desconto}
                    onChange={(e) =>
                      updateRow(row.id, "desconto", e.target.value)
                    }
                    disabled={row.status === "done"}
                    placeholder="0,00"
                    className="text-right font-mono"
                    maxWidth="54px"
                  />
                  <In
                    value={row.acrescimo}
                    onChange={(e) =>
                      updateRow(row.id, "acrescimo", e.target.value)
                    }
                    disabled={row.status === "done"}
                    placeholder="0,00"
                    className="text-right font-mono"
                    maxWidth="54px"
                  />
                  <In
                    value={row.tarifa}
                    onChange={(e) =>
                      updateRow(row.id, "tarifa", e.target.value)
                    }
                    disabled={row.status === "done"}
                    placeholder="0,00"
                    className="text-right font-mono"
                    maxWidth="54px"
                  />
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "60px" }}
                    >
                      <select
                        value={row.modoLancamento}
                        onChange={(e) =>
                          updateRow(row.id, "modoLancamento", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className={`${SEL} truncate`}
                        style={{ maxWidth: "58px" }}
                      >
                        <option value="UNICO">ÚN.</option>
                        <option value="PARCELADO">PARC.</option>
                        <option value="RECORRENTE">REC.</option>
                      </select>
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "128px" }}
                    >
                      {row.modoLancamento === "PARCELADO" ? (
                        <span className="flex items-center justify-center w-full gap-0.5">
                          <input
                            type="number"
                            min="1"
                            value={row.parcelaNumero}
                            onChange={(e) =>
                              updateRow(row.id, "parcelaNumero", e.target.value)
                            }
                            disabled={row.status === "done"}
                            placeholder="N"
                            className="w-[18px] px-0.5 py-1 text-[8px] text-center border border-gray-200 rounded focus:border-[#20243B] outline-none disabled:bg-transparent disabled:border-transparent"
                          />
                          <span className="text-[8px] text-[#9ca3af]">/</span>
                          <input
                            type="number"
                            min="1"
                            value={row.parcelaTotal}
                            onChange={(e) =>
                              updateRow(row.id, "parcelaTotal", e.target.value)
                            }
                            disabled={row.status === "done"}
                            placeholder="T"
                            className="w-[18px] px-0.5 py-1 text-[8px] text-center border border-gray-200 rounded focus:border-[#20243B] outline-none disabled:bg-transparent disabled:border-transparent"
                          />
                        </span>
                      ) : row.modoLancamento === "RECORRENTE" ? (
                        <>
                          <select
                            value={row.recorrencia}
                            onChange={(e) =>
                              updateRow(row.id, "recorrencia", e.target.value)
                            }
                            disabled={row.status === "done"}
                            className={`${SEL}`}
                            style={{ maxWidth: "80px" }}
                          >
                            <option value="Mensal">Mensal</option>
                            <option value="Bimestral">Bimestral</option>
                            <option value="Trimestral">Trimestral</option>
                            <option value="Semestral">Semestral</option>
                            <option value="Anual">Anual</option>
                          </select>
                          <input
                            type="number"
                            min="1"
                            max="60"
                            value={row.repeticoes}
                            onChange={(e) =>
                              updateRow(row.id, "repeticoes", e.target.value)
                            }
                            disabled={row.status === "done"}
                            placeholder="12"
                            className="w-[44px] px-1 py-1 text-[9px] text-center border border-gray-200 rounded focus:border-[#20243B] outline-none disabled:bg-transparent disabled:border-transparent"
                          />
                        </>
                      ) : (
                        <span className="w-full text-center text-[#d1d5db] text-[10px] font-mono select-none">
                          —
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "60px" }}
                    >
                      <select
                        value={row.statusType}
                        onChange={(e) =>
                          updateRow(row.id, "statusType", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className={SEL}
                      >
                        <option value="PAID">PAGO</option>
                        <option value="PENDING">PEND.</option>
                        <option value="FORECAST">PREV.</option>
                      </select>
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "52px" }}
                    >
                      <div className="relative">
                        <input
                          type="text"
                          value={formatCompDate(row.competenceDate)}
                          readOnly
                          disabled={row.status === "done"}
                          className="w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus-within:border-[#20243B] focus-within:ring-1 focus-within:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151] cursor-pointer"
                          onClick={() => {
                            const inp = document.getElementById(
                              `comp-hidden-${row.id}`,
                            );
                            if (inp) inp.showPicker();
                          }}
                        />
                        <input
                          id={`comp-hidden-${row.id}`}
                          type="month"
                          value={row.competenceDate}
                          onChange={(e) =>
                            updateRow(row.id, "competenceDate", e.target.value)
                          }
                          disabled={row.status === "done"}
                          className="absolute inset-0 opacity-0 pointer-events-none"
                        />
                      </div>
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "48px" }}
                    >
                      <input
                        type="text"
                        value={row.numeroDocumento}
                        onChange={(e) =>
                          updateRow(row.id, "numeroDocumento", e.target.value)
                        }
                        disabled={row.status === "done"}
                        placeholder="NF"
                        className="w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                      />
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "79px" }}
                    >
                      <input
                        type="date"
                        value={row.dataEmissao}
                        onChange={(e) =>
                          updateRow(row.id, "dataEmissao", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className="w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                      />
                    </div>
                  </td>
                  <td className={TD}>
                    <div
                      className="flex items-center gap-0.5 overflow-hidden"
                      style={{ maxWidth: "79px" }}
                    >
                      <input
                        type="date"
                        value={row.settlementDate}
                        onChange={(e) =>
                          updateRow(row.id, "settlementDate", e.target.value)
                        }
                        disabled={row.status === "done"}
                        className="w-full px-1 py-1 text-[9px] border border-gray-200 rounded focus:border-[#20243B] focus:ring-1 focus:ring-[#20243B]/20 outline-none disabled:bg-transparent disabled:border-transparent disabled:text-[#374151]"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <button
          onClick={addRow}
          className="px-2 py-1 text-[10px] font-semibold text-[#6b7280] hover:text-[#20243B] hover:bg-[#f3f4f6] rounded-md flex items-center gap-1 transition-colors"
        >
          <i className="fas fa-plus text-[9px]" />
          Adicionar linha
        </button>
        <button
          onClick={handleConfirmAll}
          disabled={pendingCount === 0 || saving}
          className={`px-3 py-1.5 text-[10px] font-bold text-white rounded-md flex items-center gap-1.5 transition-all ${pendingCount === 0 || saving ? "bg-[#9ca3af] cursor-not-allowed" : "bg-[#C5A059] hover:bg-[#b8934a] shadow-sm"}`}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin text-[9px]" />
              Lançando...
            </>
          ) : (
            <>
              <i className="fas fa-check-circle text-[9px]" />
              Confirmar {pendingCount > 0 ? `(${pendingCount})` : "Tudo"}
            </>
          )}
        </button>
      </div>

      {confirmTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-[420px] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <h4 className="text-[12px] font-bold text-[#20243B] flex items-center gap-2">
                <i className="fas fa-sync text-[#C5A059]" />
                Atualizar Template?
              </h4>
              <button
                onClick={() => setConfirmTarget(null)}
                className="text-[#9ca3af] hover:text-[#dc2626] text-[14px]"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-[11px] text-gray-600">
                O template <strong>{confirmTarget.templateId}</strong> será
                renomeado para:
              </p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-[10px] font-mono text-[#20243B]">
                {confirmTarget.newLabel}
              </div>
              <p className="text-[10px] text-emerald-600">
                ✅ O label será persistido no Firestore e visível em todas as
                sessões.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-gray-200 bg-[#f9fafb]">
              <button
                onClick={() => setConfirmTarget(null)}
                className="px-3 py-1 text-[10px] font-semibold text-[#6b7280] hover:text-[#374151] rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setTemplateLabels((prev) => ({
                    ...prev,
                    [confirmTarget.rowId]: {
                      ...(prev[confirmTarget.rowId] || {}),
                      [confirmTarget.templateId]: confirmTarget.newLabel,
                    },
                  }));
                  setConfirmedTemplates((prev) => ({
                    ...prev,
                    [confirmTarget.rowId]: {
                      ...(prev[confirmTarget.rowId] || {}),
                      [confirmTarget.templateId]: true,
                    },
                  }));
                  // Persiste no Firestore via callback
                  if (typeof onSaveTemplateLabel === "function") {
                    onSaveTemplateLabel(
                      confirmTarget.templateId,
                      confirmTarget.newLabel,
                      {
                        n1: confirmTarget.n1,
                        n2: confirmTarget.n2,
                        n3: confirmTarget.n3,
                      },
                    );
                  }
                  setConfirmTarget(null);
                }}
                className="px-3 py-1 text-[10px] font-bold text-white bg-[#C5A059] hover:bg-[#b8934a] rounded-md flex items-center gap-1 transition-colors"
              >
                <i className="fas fa-check text-[9px]" />
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {showPromptModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-[700px] max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <h4 className="text-[12px] font-bold text-[#20243B] flex items-center gap-2">
                <i className="fas fa-robot text-[#C5A059]" />
                Preencher Planilha via IA
              </h4>
              <button
                onClick={() => setShowPromptModal(false)}
                className="text-[#9ca3af] hover:text-[#dc2626] text-[14px]"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-3">
              <p className="text-[10px] text-[#6b7280]">
                Cole os dados em <strong>JSON</strong>.
              </p>
              <textarea
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder='[{"date":"2026-05-28","valorReal":"70,14","beneficiary":"Laskoski","natureza":"PF","templateId":"PET-01"}]'
                rows={10}
                className="w-full px-3 py-2 text-[11px] font-mono border border-gray-300 rounded-lg focus:border-[#20243B] focus:ring-2 focus:ring-[#20243B]/20 outline-none resize-y"
              />
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-gray-200 bg-[#f9fafb]">
              <button
                onClick={() => {
                  setShowPromptModal(false);
                  setPromptText("");
                }}
                className="px-3 py-1 text-[10px] font-semibold text-[#6b7280] hover:text-[#374151] rounded-md transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleParsePrompt}
                disabled={!promptText.trim()}
                className="px-3 py-1 text-[10px] font-bold text-white bg-[#20243B] hover:bg-[#1a1e30] rounded-md flex items-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <i className="fas fa-magic text-[9px]" />
                Processar e Preencher
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default BatchImportPanel;
